import { LogOut, Settings, ShieldAlert } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../context/AuthContext.js';
import { useClickOutside } from '../hooks/useClickOutside.js';
import { Avatar } from './Avatar.js';
import { SettingsModal } from './SettingsModal.js';
import './UserMenu.css';

export function UserMenu() {
  const { t } = useTranslation();
  const { user, logout, isImpersonating } = useAuth();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useClickOutside(menuRef, () => setOpen(false));

  if (!user) return null;

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        type="button"
        className="user-menu-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar name={user.displayName} avatarUrl={user.avatarUrl} size={32} />
        {isImpersonating && <ShieldAlert size={13} className="user-menu-impersonation-dot" />}
      </button>

      {open && (
        <div className="user-menu-panel" role="menu">
          <div className="user-menu-header">
            <Avatar name={user.displayName} avatarUrl={user.avatarUrl} size={40} />
            <div>
              <p className="user-menu-name">{user.displayName}</p>
              <p className="user-menu-username">@{user.username}</p>
            </div>
          </div>
          {isImpersonating && (
            <p className="user-menu-impersonation-note">
              {t('auth.impersonating', { username: user.username })}
            </p>
          )}
          <button
            type="button"
            className="user-menu-item"
            onClick={() => {
              setSettingsOpen(true);
              setOpen(false);
            }}
          >
            <Settings size={16} /> {t('nav.settings')}
          </button>
          <button type="button" className="user-menu-item user-menu-item-danger" onClick={logout}>
            <LogOut size={16} /> {isImpersonating ? t('auth.stopImpersonating') : t('nav.logout')}
          </button>
        </div>
      )}

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
